import { CheckCircleIcon, ClockIcon, XCircleIcon } from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import mesaLogo from '@/assets/mesa_logo_light_circle.webp';
import { ErrorState } from '@/components/error-state';
import { Button } from '@/components/ui/button';
import { CenteredSpinner } from '@/components/ui/centered-spinner';
import { sessionQueryOptions } from '@/lib/auth-query-options';
import {
  acceptInvitationServerFn,
  getInvitationServerFn,
  rejectInvitationServerFn,
} from '@/server/functions/organization';

export const Route = createFileRoute('/invite/$invitationId')({
  ssr: 'data-only',
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const session = await queryClient.ensureQueryData(sessionQueryOptions);

    if (!session) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: `/invite/${params.invitationId}` },
      });
    }

    return { session };
  },
  component: InvitePage,
});

function InvitePage() {
  const { invitationId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data: invitation,
    isLoading,
    error: invitationError,
    refetch,
  } = useQuery({
    queryKey: ['invite', invitationId],
    queryFn: () => getInvitationServerFn({ data: { invitationId } }),
  });

  const isExpired = invitation?.expired ?? false;

  const handleAccept = async () => {
    if (!invitation) return;
    setIsAccepting(true);
    setError(null);
    try {
      const { orgSlug } = await acceptInvitationServerFn({
        data: { invitationId: invitation.id },
      });
      await queryClient.invalidateQueries({ queryKey: ['auth-orgs'] });
      navigate({ to: '/$orgSlug/repositories', params: { orgSlug } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!invitation) return;
    setIsRejecting(true);
    setError(null);
    try {
      await rejectInvitationServerFn({ data: { invitationId: invitation.id } });
      navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setIsRejecting(false);
    }
  };

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (invitationError) {
    return <ErrorState error={new Error('Invitation not available')} reset={refetch} />;
  }

  if (!invitation) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="h-2 bg-mesa-green" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8 text-center">
            <XCircleIcon className="mx-auto mb-4 h-12 w-12 text-mesa-red" />
            <h1 className="text-xl font-semibold mb-2">Invitation not found</h1>
            <p className="text-mesa-muted mb-6">This invitation may have been cancelled or already used.</p>
            <Button
              onClick={() => {
                navigate({ to: '/' });
              }}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
        <div className="h-2 bg-mesa-green" />
      </div>
    );
  }

  if (invitation.status !== 'pending') {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="h-2 bg-mesa-green" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8 text-center">
            <CheckCircleIcon className="mx-auto mb-4 h-12 w-12 text-mesa-green" />
            <h1 className="text-xl font-semibold mb-2">Invitation already {invitation.status}</h1>
            <p className="text-mesa-muted mb-6">This invitation has already been {invitation.status}.</p>
            <Button
              onClick={() => {
                navigate({ to: '/' });
              }}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
        <div className="h-2 bg-mesa-green" />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="h-2 bg-mesa-green" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8 text-center">
            <ClockIcon className="mx-auto mb-4 h-12 w-12 text-mesa-muted" />
            <h1 className="text-xl font-semibold mb-2">Invitation expired</h1>
            <p className="text-mesa-muted mb-6">
              This invitation has expired. Please ask the organization owner to send a new one.
            </p>
            <Button
              onClick={() => {
                navigate({ to: '/' });
              }}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
        <div className="h-2 bg-mesa-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="h-2 bg-mesa-green" />
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md border border-mesa-border bg-mesa-card p-8">
          <div className="text-center mb-6">
            <img src={mesaLogo} alt="Mesa" className="mx-auto mb-4 h-12 w-12" />
            <h1 className="text-xl font-semibold mb-2">You're invited!</h1>
            <p className="text-mesa-muted">
              You've been invited to join <strong>{invitation.organizationName}</strong>
            </p>
          </div>

          <div className="border border-mesa-border bg-mesa-cream-dark p-4 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-mesa-muted">Organization</span>
              <span className="font-medium">{invitation.organizationName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-mesa-muted">Role</span>
              <span className="font-medium capitalize">{invitation.role ?? 'Member'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-mesa-muted">Invited by</span>
              <span className="font-medium">{invitation.inviterEmail}</span>
            </div>
          </div>

          {error && (
            <div className="border border-mesa-red/30 bg-mesa-red/10 p-3 text-sm text-mesa-red mb-4">{error}</div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={handleReject} disabled={isAccepting || isRejecting}>
              {isRejecting ? 'Declining...' : 'Decline'}
            </Button>
            <Button className="flex-1" onClick={handleAccept} disabled={isAccepting || isRejecting}>
              {isAccepting ? 'Joining...' : 'Accept & Join'}
            </Button>
          </div>
        </div>
      </div>
      <div className="h-2 bg-mesa-green" />
    </div>
  );
}
